import { PrototypeApiError } from "../apiClient";
import { handleApiError } from "../authGuards";
import { inputValue, selectedCategory, textareaValue } from "../domUtils";
import type { PlanCode, PrototypeRuntime, Root } from "../prototypeTypes";
import { clearFieldError, showFieldError } from "../wizardValidation";
import { getRecoverableTurnstileToken } from '../turnstileRuntime';
import { hasPendingFeedbackAttachmentUpload } from '../feedbackAttachmentUploadState';
import { feedbackErrorMessage } from '../feedbackErrorPresentation';
import { resetFeedbackForm } from '../formProfileController';
import {
	beginLongFormSubmission,
	captureFeedbackDraft,
	clearLongFormSubmissionDraft,
	isLatestLongFormSubmission,
	ownsLongFormUi,
	sameDraft,
	submissionKeyForDraft,
} from './longFormDraftLifecycle';

export async function submitFeedback(
	root: Root,
	runtime: PrototypeRuntime,
	handlers: {
		showFeedbackSuccess(root: Root, isLoggedIn: boolean): void;
		failValidation(message: string): false;
		notifyPreservedDraftSuccess?(): void;
		recordRuntimeError(runtime: PrototypeRuntime, error: unknown): void;
		currentPlan: PlanCode;
		isLoggedIn: boolean;
	},
) {
	const category = selectedCategory(root, "view-feedback") || "other";
	const subject = inputValue(root, "feedbackSubject").trim();
	const details = textareaValue(root, "feedbackBody").trim();
	const attachmentKey = root.querySelector<HTMLElement>("#feedbackFilePreview")?.getAttribute("data-uploaded-key") || undefined;
	const attachmentSubmissionId = root.querySelector<HTMLElement>("#feedbackFilePreview")?.getAttribute("data-uploaded-submission-id") || undefined;
	const submissionHost = root.querySelector<HTMLElement>('#view-feedback');
	if (hasPendingFeedbackAttachmentUpload(root)) return handlers.failValidation('Your screenshot is still uploading. Please wait for it to finish.');
	let hasError = false;
	if (subject.length < 3 || subject.length > 120) {
		showFieldError(root, "feedbackSubject", subject.length === 0 ? "Please enter a subject with at least 3 characters." : subject.length > 120 ? "Subject must not exceed 120 characters." : "Subject must be at least 3 characters.");
		hasError = true;
	} else clearFieldError(root, "feedbackSubject");
	if (details.length < 10 || details.length > 2000) {
		showFieldError(root, "feedbackBody", details.length === 0 ? "Please describe your feedback with at least 10 characters." : details.length > 2000 ? "Details must not exceed 2000 characters." : "Details must be at least 10 characters.");
		hasError = true;
	} else clearFieldError(root, "feedbackBody");
	if (hasError) return handlers.failValidation("Please review the highlighted fields and try again.");
	const snapshot = captureFeedbackDraft(root);
	const fingerprint = { category, subject, details, attachmentKey: attachmentKey ?? null, attachmentSubmissionId: attachmentSubmissionId ?? null };
	const submissionId = submissionHost ? submissionKeyForDraft(submissionHost, 'submissionId', fingerprint) : crypto.randomUUID();
	const submission = submissionHost ? beginLongFormSubmission(submissionHost, snapshot) : null;
	const submitBtn = root.querySelector<HTMLElement>('#feedbackSubmitBtn');
	const originalText = submitBtn?.textContent ?? '';
	if (submitBtn) {
		submitBtn.textContent = 'Submitting...';
		submitBtn.setAttribute('data-locked', '');
	}

	try {
		const turnstileToken = await getRecoverableTurnstileToken(runtime, 'feedback-submit');
		await runtime.api.submitFeedback({ submissionId, category, subject, details, attachmentKey, attachmentSubmissionId, turnstileToken });
		const settlesDraft = Boolean(submissionHost && submission && isLatestLongFormSubmission(submissionHost, submission.sequence) && sameDraft(captureFeedbackDraft(root), snapshot));
		const ownsUi = Boolean(submissionHost && submission && ownsLongFormUi(submissionHost, submission.attempt));
		if (settlesDraft) {
			resetFeedbackForm(root, { presentation: false, resetSubmitButton: ownsUi });
			clearLongFormSubmissionDraft(submissionHost);
		}
		if (settlesDraft && ownsUi) handlers.showFeedbackSuccess(root, handlers.isLoggedIn);
		else if (ownsUi) handlers.notifyPreservedDraftSuccess?.();
	} catch (error) {
		const ownsUi = submissionHost && submission ? ownsLongFormUi(submissionHost, submission.attempt) : true;
		if (!ownsUi) return;
		if (error instanceof PrototypeApiError && (error.status === 401 || error.status === 403)) {
			handleApiError(runtime, handlers.currentPlan, handlers.isLoggedIn, error, { root, intent: "feedback" });
			return;
		}
		handlers.recordRuntimeError(runtime, error);
		handlers.failValidation(feedbackErrorMessage(error, 'Feedback submission failed. Please try again.'));
	} finally {
		if (submissionHost && submission && ownsLongFormUi(submissionHost, submission.attempt)) {
			delete submissionHost.dataset.submitting;
			if (submitBtn) {
				submitBtn.textContent = originalText;
				submitBtn.removeAttribute('data-locked');
			}
		}
	}
}
